export function isAbortError(e: unknown): boolean {
  if (e == null || e === undefined) return false;

  if (typeof DOMException !== "undefined" && e instanceof DOMException) {
    if (e.name === "AbortError") return true;
  }

  if (
    typeof e === "object" &&
    "name" in e &&
    (e as { name?: string }).name === "AbortError"
  ) {
    return true;
  }

  const msg =
    typeof e === "string"
      ? e
      : typeof e === "object" && e !== null && "message" in e
        ? String((e as { message?: unknown }).message ?? "")
        : "";

  const lower = msg.toLowerCase();
  if (lower.includes("signal is aborted")) return true;
  if (lower.includes("the user aborted a request")) return true;

  return false;
}

export function isTimeoutError(e: unknown): boolean {
  return (
    e instanceof DOMException &&
    (e.name === "TimeoutError" || e.message.toLowerCase().includes("timed out"))
  );
}

export const REQUEST_TIMEOUT_MS = 10_000;

/**
 * Единый выключатель всего механизма отмены запросов.
 *
 * Когда `true`, `combineWithTimeout` не навешивает на реальный `fetch` ни один
 * AbortSignal, поэтому НИ ОДИН запрос не может быть отменён: ни таймаутом, ни
 * сменой роута (getRouteSignal), ни размонтированием/сбросом компонента
 * (useAbortController), ни контроллерами в event-сторах.
 *
 * Весь остальной код, связанный с AbortController (контроллеры, вызовы
 * `.abort()`, проверки `signal.aborted` и `isAbortError`), остаётся на месте,
 * но не влияет на сетевые запросы. Чтобы вернуть отмену — поставьте `false`.
 */
export const ABORT_DISABLED = true

export function combineWithTimeout(
  signal?: AbortSignal | null,
  // Тестовый seam: пока ABORT_DISABLED === true, ветки ниже недостижимы в
  // проде; тесты передают false, чтобы покрыть логику комбинирования сигналов.
  abortDisabled: boolean = ABORT_DISABLED,
): AbortSignal | undefined {
  if (abortDisabled) return undefined;

  const timeoutController = new AbortController();
  const timeoutId = setTimeout(
    () => timeoutController.abort(),
    REQUEST_TIMEOUT_MS,
  );
  const combined = signal
    ? AbortSignal.any([signal, timeoutController.signal])
    : timeoutController.signal;

  if (combined.aborted) {
    clearTimeout(timeoutId);
  } else {
    combined.addEventListener("abort", () => clearTimeout(timeoutId), {
      once: true,
    });
  }

  return combined;
}
